import HBIcon from "@/components/HBIcon";
import HBSelect from "@/components/HBInput/select";
import HBTabButton from "@/components/HBTabButton";
import useAppStore from "@/context/store";
import { setSearch } from "@/tracker/shared/global";
import filterUrlParams from "@/utils/filterUrlParams";
import useSideNavCollapse from "@/utils/useSideNavCollapse";
import { Badge, Checkbox, Radio } from "antd";
import classNames from "classnames";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import styles from "./FilterPanel.module.less";

enum TriggerType {
  click = "click",
  hover = "hover",
}

interface FilterOption {
  name: string;
  value?: {
    label: string;
    value: string;
    icon?: string;
  };
  options: Array<{
    label: string;
    value: string;
    icon?: string;
    optionLabel?: string;
  }>;
  key: string;
  allowClear?: boolean;
  optionType?: "text" | "img";
  iconType?: "iconfont" | "img";
  multiple?: boolean;
  onChange?: (value: any) => void;
}

interface FilterPanelProps {
  filters: FilterOption[];
  visible: boolean;
  onClose: () => void;
  groupID?: number;
  tabList?: Array<{
    name: string | React.ReactNode;
    value: string;
    href?: string;
    trackerProps?: Record<string, string>;
  }>;
  onTabChange?: (option: any) => void;
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  filters = [],
  visible,
  onClose,
  groupID = 0,
  tabList = [],
  onTabChange,
}) => {
  const router = useRouter();
  const [isCollapsed] = useSideNavCollapse();
  const { getSelectedCount, setSelectedFilters, clearSelectedFilters, user } =
    useAppStore((state) => ({
      getSelectedCount: state.getSelectedCount,
      setSelectedFilters: state.setSelectedFilters,
      clearSelectedFilters: state.clearSelectedFilters,
      user: state.user,
    }));

  const [selectedValues, setSelectedValues] = useState<Record<string, any>>(
    () => {
      return filters.reduce((acc, filter) => {
        if (filter.value) {
          acc[filter.key] = filter.value;
        }
        return acc;
      }, {});
    }
  );

  // 使用useEffect处理初始化，但只在组件挂载时执行一次
  useEffect(() => {
    const selectedFilters = Object.entries(selectedValues).reduce(
      (acc, [key, value]) => {
        if (key === "sort" && (value as any)?.value === "all") {
          return acc;
        }
        // 排除view类型的筛选器
        if (key === "view") {
          return acc;
        }
        acc[key] = value;
        return acc;
      },
      {}
    );
    setSelectedFilters(selectedFilters);
  }, []); // 空依赖数组确保只在挂载时执行一次

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => {
    // 初始化时将所有筛选项设置为展开状态
    return filters.reduce((acc, filter) => {
      acc[filter.key] = true;
      return acc;
    }, {});
  });
  const [active, setActive] = useState<number>(null);
  const [groupActive, setGroupActive] = useState<boolean>(false);
  const [filterList, setFilterList] = useState([]);
  const panelRef = useRef(null);

  useEffect(() => {
    if (visible) {
      setFilterList(
        Array.from(
          document.querySelectorAll(`[data-filter-id=filter-${groupID}]`)
        )
      );
    }
  }, [visible, groupID]);

  const changeActiveFilter = (index, trigger, e) => {
    if (TriggerType.click === trigger) {
      setActive(active === index ? null : index);
    } else {
      setActive(index);
    }
  };

  useEffect(() => {
    setGroupActive(active !== null);
  }, [active]);

  const handleReset = () => {
    // 重置时保留sort=all
    const sortFilter = filters.find((f) => f.key === "sort");
    const resetValues = sortFilter
      ? {
          sort: sortFilter.options.find((opt) => opt.value === "all"),
        }
      : {};

    setSelectedValues(resetValues);
    setSelectedFilters({}); // 清空selectedFilters，因为sort=all不计入选中数量

    // 重置时清除URL中的filter_ids参数，但保留sort=all
    const query = { ...router.query };
    delete query.filter_ids;
    delete query.category;
    delete query.sort;

    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    );
  };

  const handleValueChange = (key: string, value: any) => {
    setSearch({
      search_type: "筛选器-点击",
    });
    const newValues = {
      ...selectedValues,
      [key]: value,
    };
    setSelectedValues(newValues);

    // 更新selectedFilters，但排除sort=all和view的情况
    const selectedFilters = Object.entries(newValues).reduce((acc, [k, v]) => {
      // 排除sort=all、值为null、分类选择全部、以及view类型的情况
      if (
        (k === "sort" && (v as any)?.value === "all") ||
        !v ||
        (k === "category" && (v as any)?.value === "all") ||
        k === "view"
      ) {
        return acc;
      }
      acc[k] = v;
      return acc;
    }, {});
    setSelectedFilters(selectedFilters);

    // 更新URL参数
    const query = { ...router.query };

    // 视图类型特殊处理
    if (key === "view") {
      // 直接调用父组件传入的onChange处理函数
      const filter = filters.find((f) => f.key === "view");
      if (filter?.onChange) {
        filter.onChange(value);
      }
      return;
    }

    // 排序参数特殊处理
    if (key === "sort") {
      if (value?.value) {
        query.sort = value.value;
      } else {
        delete query.sort;
      }
    } else if (key === "category" && router.query.type === "board") {
      if (value) {
        if (value.value === "all") {
          delete query.category;
        } else {
          query.category = value.value;
        }
      } else {
        delete query.category;
      }
    } else {
      // 其他筛选参数使用filter_ids
      const filterIdsMap = filterUrlParams.decode(
        (query.filter_ids as string) || ""
      );

      if (value) {
        // 如果是分类且选择全部，则删除该分类参数
        if (key === "category" && value.value === "all") {
          delete filterIdsMap[key];
        } else {
          filterIdsMap[key] = value.value;
        }
      } else {
        delete filterIdsMap[key];
      }

      if (Object.keys(filterIdsMap).length > 0) {
        query.filter_ids = filterUrlParams.encode(filterIdsMap);
      } else {
        delete query.filter_ids;
      }
    }

    router.push(
      {
        pathname: router.pathname,
        query,
      },
      undefined,
      { shallow: true }
    );
  };

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const selectedCount = getSelectedCount();

  // 监听filters变化，重置展开状态
  useEffect(() => {
    // 检查是否是tag切换导致的filters更新
    const isTabChange = filters.some((filter) => {
      const prevFilter = Object.keys(expandedSections).find(
        (key) => key === filter.key
      );
      return !prevFilter;
    });

    if (isTabChange) {
      // 重置展开状态
      setExpandedSections(
        filters.reduce((acc, filter) => {
          acc[filter.key] = true;
          return acc;
        }, {})
      );
    }

    // 设置默认值
    const defaultValues = filters.reduce((acc, filter) => {
      if (filter.value) {
        acc[filter.key] = filter.value;
      }
      return acc;
    }, {});
    setSelectedValues(defaultValues);
  }, [filters]);

  return (
    <div
      ref={panelRef}
      className={classNames(styles.filterPanelContainer, {
        [styles.visible]: visible,
        [styles.collapsed]: isCollapsed,
      })}
      style={{
        paddingBottom: !user && isCollapsed ? "84px" : "0",
      }}
    >
      {/* 第一块：选中项数量展示 */}
      <div className={styles.selectedCountSection}>
        <div className={styles.filterTitleWrapper}>
          <h2 className={styles.filterTitle}>筛选</h2>
          <div className={styles.badgeWrapper}>
            <Badge count={selectedCount} size="default"></Badge>
          </div>
        </div>
        <button
          type="button"
          onClick={handleReset}
          className={styles.resetButton}
          disabled={selectedCount === 0}
        >
          重置
        </button>
      </div>

      {/* 添加HBTabButton作为顶部的单选项 */}
      <div className={styles.filterSection}>
        <HBTabButton
          options={tabList}
          value={(router.query.type as string) || "pin"}
          onChange={(option) => {
            // 重置筛选选项
            setSelectedValues({});
            clearSelectedFilters();
            // 调用原有的onTabChange
            onTabChange?.(option);
          }}
          renderType="radio"
          className={""}
          size="small"
        />
      </div>

      {/* 动态渲染筛选区块 */}
      {filters.map((filter, index) => (
        <div
          key={filter.key}
          className={styles.filterSection}
          onClick={(e) => changeActiveFilter(index, TriggerType.click, e)}
          onMouseEnter={(e) =>
            groupActive && changeActiveFilter(index, TriggerType.hover, e)
          }
          data-filter-id={`filter-${groupID}`}
        >
          <div
            className={styles.sectionHeader}
            onClick={(e) => {
              if (filter.key === "category") {
                return;
              }
              e.stopPropagation();
              toggleSection(filter.key);
            }}
          >
            <h3 className={styles.filterTitle}>{filter.name}</h3>
            {filter.key !== "category" && (
              <button
                className={classNames(styles.expandButton, {
                  [styles.expanded]: expandedSections[filter.key],
                })}
              >
                <HBIcon type="ic_chevron_right" className={styles.icon} />
              </button>
            )}
          </div>
          {!expandedSections[filter.key] && (
            <div className={styles.selectedValue}>
              {selectedValues[filter.key] ? (
                filter.optionType === "img" ? (
                  <div className={styles.selectedImages}>
                    {Array.isArray(selectedValues[filter.key]) ? (
                      selectedValues[filter.key].map((v, index) => (
                        <img
                          key={index}
                          src={v.icon}
                          alt={v.label}
                          className={styles.selectedImage}
                        />
                      ))
                    ) : (
                      <img
                        src={selectedValues[filter.key].icon}
                        alt={selectedValues[filter.key].label}
                        className={styles.selectedImage}
                      />
                    )}
                  </div>
                ) : Array.isArray(selectedValues[filter.key]) ? (
                  selectedValues[filter.key].map((v) => v.label).join("、")
                ) : (
                  selectedValues[filter.key].label
                )
              ) : (
                "不限"
              )}
            </div>
          )}
          {expandedSections[filter.key] &&
            filter.options.length > 0 &&
            (filter.key === "category" ? (
              <HBSelect
                value={selectedValues[filter.key]?.value || "all"}
                onChange={(value) =>
                  handleValueChange(filter.key, {
                    value,
                    label:
                      value === "all"
                        ? "全部"
                        : filter.options.find((opt) => opt.value === value)
                            ?.label,
                  })
                }
                options={[{ value: "all", label: "全部" }, ...filter.options]}
                wrapClassName={styles.categorySelectWrap}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                noWrapper={true}
              />
            ) : filter.optionType === "img" ? ( // 图片类型，如颜色筛选器
              <div className={styles.imgGrid}>
                {filter.options.map((option) => (
                  <div
                    key={option.value}
                    className={classNames(styles.imgItem, {
                      [styles.active]:
                        selectedValues[filter.key]?.value === option.value,
                    })}
                    onClick={() => {
                      if (selectedValues[filter.key]?.value === option.value) {
                        handleValueChange(filter.key, null);
                      } else {
                        handleValueChange(filter.key, option);
                      }
                    }}
                  >
                    <img src={option.icon} alt={option.label} />
                  </div>
                ))}
              </div>
            ) : filter.multiple ? ( // 多选类型，如格式筛选器
              <Checkbox.Group
                value={selectedValues[filter.key]?.map((v) => v.value) || []}
                onChange={(values) =>
                  handleValueChange(
                    filter.key,
                    filter.options.filter((opt) => values.includes(opt.value))
                  )
                }
                className={styles.checkboxGroup}
              >
                {filter.options.map((option) => (
                  <Checkbox key={option.value} value={option.value}>
                    {option.optionLabel || option.label}
                  </Checkbox>
                ))}
              </Checkbox.Group>
            ) : (
              // 单选类型，如排序筛选器
              <Radio.Group
                value={
                  filter.key === "sort" || filter.key === "view"
                    ? selectedValues[filter.key]?.value
                    : selectedValues[filter.key]?.value || "no-limit"
                }
                className={classNames(styles.radioGroup, {
                  [styles.grid]:
                    filter.key === "file_type" ||
                    filter.key === "material_source",
                })}
              >
                {filter.key !== "sort" && filter.key !== "view" && (
                  <Radio
                    key="no-limit"
                    value="no-limit"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleValueChange(filter.key, null);
                    }}
                  >
                    不限
                  </Radio>
                )}
                {filter.options.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleValueChange(filter.key, option);
                    }}
                  >
                    {filter.key === "resolution" ? (
                      // 尺寸筛选器
                      <div className={styles.resolutionItem}>
                        <div>{option?.optionLabel?.split(" ")?.[0]}</div>
                        <div className={styles.resolutionLabel}>
                          {option?.optionLabel?.split(" ")?.[1]}
                        </div>
                      </div>
                    ) : (
                      option.optionLabel || option.label
                    )}
                  </Radio>
                ))}
              </Radio.Group>
            ))}
        </div>
      ))}
    </div>
  );
};

export default FilterPanel;
